import React, { useContext } from 'react';
import { useTranslation } from 'next-i18next';

import Hr from '@common/components/Hr';
import { getAllCountriesOptions } from '@common/constants/allCountries';
import { getIdentityDocumentsTypes } from '@common/constants/identityDocuments';
import { formatBirthDate } from '../../MobileOrderForm.helpers';
import { FormContext } from '../../FormWrapper';
import { GENDER_FEMALE } from '@common/constants/genders';
import { LANGUAGE_DE } from '@common/constants/languages';

import {
  Title,
  Table,
  TableRow,
  TableRowElement,
  TableRowHeading,
} from './SummaryFormSection.styled';

const SECTION_TRANSLATION_PREFIX = 'mobile.orderForm.summary';

const SummaryFormSection = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { formik } = useContext(FormContext);

  const countries = getAllCountriesOptions(t);
  const identityDocuments = getIdentityDocumentsTypes(t);

  const {
    values: {
      gender,
      first_name,
      last_name,
      date_of_birth,
      contact_phone_number,
      email,
      nationality,
      id_type,
      id_number,
      roaming,
      delivery_address,
      billing_address,
    },
  } = formik;

  return (
    <React.Fragment>
      <Title spacingBottom={['scale', 3]}>
        {t(`${SECTION_TRANSLATION_PREFIX}.declarationsSectionTitle`, 'Angaben')}
      </Title>
      <Table withoutGapMobile>
        <TableRow>
          <TableRowElement>{`${first_name} ${last_name}`}</TableRowElement>
          <TableRowElement>
            {date_of_birth &&
              formatBirthDate(
                date_of_birth,
                `dd${language === LANGUAGE_DE ? '.' : ''} MMMM yyyy`,
                language,
              )}
          </TableRowElement>
        </TableRow>
        <TableRow isWider>
          <TableRowElement>{email}</TableRowElement>
          <TableRowElement>{contact_phone_number}</TableRowElement>
        </TableRow>
      </Table>
      <Hr />
      <Title spacingBottom={['scale', 3]}>
        {t(`${SECTION_TRANSLATION_PREFIX}.idSectionTitle`, 'Ausweis')}
      </Title>
      <Table>
        <TableRow>
          <TableRowHeading spacingBottom={['equal', 4]}>
            {t(
              `${SECTION_TRANSLATION_PREFIX}.nationalityLabel`,
              'Nationalität',
            )}
          </TableRowHeading>
          <TableRowElement>
            {countries.find(country => country.value === nationality)?.label ||
              ''}
          </TableRowElement>
        </TableRow>
        <TableRow>
          <TableRowHeading spacingBottom={['equal', 4]}>
            {t(`${SECTION_TRANSLATION_PREFIX}.idTypeLabel`, 'Ausweistyp')}
          </TableRowHeading>
          <TableRowElement>
            {identityDocuments.find(id => id.value === id_type)?.label || ''}
          </TableRowElement>
        </TableRow>
        <TableRow>
          <TableRowHeading spacingBottom={['equal', 4]}>
            {t(`${SECTION_TRANSLATION_PREFIX}.idNumberLabel`, 'Ausweisnumme')}
          </TableRowHeading>
          <TableRowElement>{id_number}</TableRowElement>
        </TableRow>
      </Table>
      <Hr />
      <Title spacingBottom={['scale', 3]}>
        {t(`${SECTION_TRANSLATION_PREFIX}.addressSectionTitle`, 'Adressen')}
      </Title>
      <Table>
        <TableRow>
          <TableRowHeading spacingBottom={['equal', 4]}>
            {t(
              `${SECTION_TRANSLATION_PREFIX}.deliveryAddressSectionTitle`,
              'Liferadresse',
            )}
          </TableRowHeading>
          <TableRowElement>
            {gender === GENDER_FEMALE
              ? t(
                  'shop.checkout.step2.address.deliveryAddress.gender.female.label',
                  'Frau',
                )
              : t(
                  'shop.checkout.step2.address.deliveryAddress.gender.male.label',
                  'Herr',
                )}
          </TableRowElement>
          <TableRowElement>{`${first_name} ${last_name}`}</TableRowElement>
          {delivery_address.suffix && (
            <TableRowElement>{delivery_address.suffix}</TableRowElement>
          )}
          <TableRowElement>
            {`${delivery_address.street} ${delivery_address.house_nr}`}
          </TableRowElement>
          <TableRowElement>
            {`${delivery_address.postal_code} ${delivery_address.place}`}
          </TableRowElement>
          <TableRowElement>
            {countries.find(
              country => country.value === delivery_address.country,
            )?.label || ''}
          </TableRowElement>
        </TableRow>
        {!billing_address.should_fill_with_delivery_data ? (
          <TableRow>
            <TableRowHeading spacingBottom={['equal', 4]}>
              {t(
                `${SECTION_TRANSLATION_PREFIX}.billingAddressSectionTitle`,
                'Rechungsadresse',
              )}
            </TableRowHeading>
            <TableRowElement>
              {billing_address.gender === GENDER_FEMALE
                ? t(
                    'shop.checkout.step2.address.deliveryAddress.gender.female.label',
                    'Frau',
                  )
                : t(
                    'shop.checkout.step2.address.deliveryAddress.gender.male.label',
                    'Herr',
                  )}
            </TableRowElement>
            <TableRowElement>
              {`${billing_address.first_name} ${billing_address.last_name}`}
            </TableRowElement>
            {billing_address.suffix && (
              <TableRowElement>{billing_address.suffix}</TableRowElement>
            )}
            <TableRowElement>
              {`${billing_address.street} ${billing_address.house_nr}`}
            </TableRowElement>
            <TableRowElement>
              {`${billing_address.postal_code} ${billing_address.place}`}
            </TableRowElement>
            <TableRowElement>
              {countries.find(
                country => country.value === billing_address.country,
              )?.label || ''}
            </TableRowElement>
          </TableRow>
        ) : (
          <TableRow>
            <TableRowHeading spacingBottom={['equal', 4]}>
              {t(
                `${SECTION_TRANSLATION_PREFIX}.billingAddressSectionTitle`,
                'Rechungsadresse',
              )}
            </TableRowHeading>
            <TableRowElement>
              {gender === GENDER_FEMALE
                ? t(
                    'shop.checkout.step2.address.deliveryAddress.gender.female.label',
                    'Frau',
                  )
                : t(
                    'shop.checkout.step2.address.deliveryAddress.gender.male.label',
                    'Herr',
                  )}
            </TableRowElement>
            <TableRowElement>{`${first_name} ${last_name}`}</TableRowElement>
            {delivery_address.suffix && (
              <TableRowElement>{delivery_address.suffix}</TableRowElement>
            )}
            <TableRowElement>
              {`${delivery_address.street} ${delivery_address.house_nr}`}
            </TableRowElement>
            <TableRowElement>
              {`${delivery_address.postal_code} ${delivery_address.place}`}
            </TableRowElement>
            <TableRowElement>
              {countries.find(
                country => country.value === delivery_address.country,
              )?.label || ''}
            </TableRowElement>
          </TableRow>
        )}
      </Table>
      <Hr />
      <Table>
        <TableRow>
          <TableRowHeading spacingBottom={['equal', 4]}>
            {t(`${SECTION_TRANSLATION_PREFIX}.roamingSectionTitle`, 'Roaming')}
          </TableRowHeading>
          <TableRowElement>
            {roaming === 'agree'
              ? t(
                  `${SECTION_TRANSLATION_PREFIX}.roamingSectionAgree`,
                  'Aktiviren',
                )
              : t(
                  `${SECTION_TRANSLATION_PREFIX}.roamingSectionDisagree`,
                  'Nicht aktivieren',
                )}
          </TableRowElement>
        </TableRow>
      </Table>
    </React.Fragment>
  );
};

export default SummaryFormSection;
